import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { StaticPageLayout } from '@/components/StaticPageLayout';

type FAQ = {
  id: number;
  question: string;
  answer: string;
};

type FAQCategory = {
  category: string;
  faqs: FAQ[];
};

type Props = {
  faqs_by_category: FAQCategory[];
};

function FAQItem({ faq }: { faq: FAQ }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 px-0 flex items-start justify-between text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
      >
        <span className="text-lg font-medium text-gray-900 pr-8">
          {faq.question}
        </span>
        <ChevronDown
          className={`h-5 w-5 text-gray-500 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-gray-600 leading-relaxed pr-12">{faq.answer}</p>
      </div>
    </div>
  );
}

export default function FaqsIndex({ faqs_by_category }: Props) {
  return (
    <StaticPageLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about Project SHORT, our
              mentorship programs, and how to get involved.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="space-y-16">
          {faqs_by_category.map((category, index) => (
            <div key={index}>
              <h2 className="text-2xl font-semibold text-gray-900 mb-8">
                {category.category}
              </h2>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="px-8">
                  {category.faqs.map((faq) => (
                    <FAQItem key={faq.id} faq={faq} />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-20 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Still have questions?
          </h3>
          <p className="text-gray-600 mb-8">
            Can't find the answer you're looking for? Please reach out to our
            team.
          </p>
          <a
            href="mailto:contact@project-short.com"
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary/90 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </StaticPageLayout>
  );
}
